import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfigData, translate } from "../../config";
import { useMessages } from "../../context/MessagesContext";
import { useRadar } from "../../context/RadarContext";
import { useSearchParamState } from "../../hooks/use-search-param-state";
import { Item, featuredOnly } from "../../model";
import {
  NumberedEntry,
  entriesOfRing,
  excerpt,
  numberedEntries,
} from "../../quadrantZoom";
import Fadeable from "../Fadeable/Fadeable";
import Flag from "../Flag/Flag";
import HeadlineGroup from "../HeadlineGroup/HeadlineGroup";
import HeroHeadline from "../HeroHeadline/HeroHeadline";
import Link, { radarHref } from "../Link/Link";
import QuadrantChart from "../QuadrantChart/QuadrantChart";
import SetTitle from "../SetTitle";
import "./page-quadrant.scss";

type PageQuadrantProps = {
  leaving: boolean;
  onLeave: () => void;
  pageName: string;
  items: Item[];
  config: ConfigData;
};

const Legend = () => (
  <ul className="page-quadrant__legend">
    <li>
      <span className="page-quadrant__legend-mark page-quadrant__legend-mark--new" />
      New this quarter
    </li>
    <li>
      <span className="page-quadrant__legend-mark page-quadrant__legend-mark--changed" />
      Moved this quarter
    </li>
    <li>
      <span className="page-quadrant__legend-mark" />
      Unchanged
    </li>
  </ul>
);

/**
 * One ring of the quadrant: its entries, numbered as they are on the chart, each
 * with the opening of its description. The number is the whole navigation - it
 * lets the eye go from a blip to a paragraph without searching.
 */
const RingSection = ({
  ring,
  description,
  entries,
  highlighted,
  onHighlight,
}: {
  ring: string;
  description?: string;
  entries: NumberedEntry[];
  highlighted?: number;
  onHighlight: (n?: number) => void;
}) => (
  <section className="page-quadrant__ring" id={`ring-${ring}`}>
    <header className="page-quadrant__ring-header">
      <h2 className="page-quadrant__ring-title">{ring}</h2>
      {description && (
        <span
          className="page-quadrant__ring-help"
          title={description}
          aria-label={description}
        >
          ?
        </span>
      )}
      <span className="page-quadrant__ring-count">
        {entries.length} {entries.length === 1 ? "entry" : "entries"}
      </span>
    </header>

    {entries.length === 0 ? (
      <p className="page-quadrant__empty">
        Nothing in this ring at the selected quarter.
      </p>
    ) : (
      <ol className="page-quadrant__entries">
        {entries.map((entry) => (
          <li
            key={entry.number}
            className={
              highlighted === entry.number
                ? "page-quadrant__entry is-highlighted"
                : "page-quadrant__entry"
            }
            onMouseEnter={() => onHighlight(entry.number)}
            onMouseLeave={() => onHighlight(undefined)}
          >
            <span className="page-quadrant__entry-number">{entry.number}</span>
            <div className="page-quadrant__entry-body">
              <h3 className="page-quadrant__entry-title">
                <Link pageName={`${entry.item.quadrant}/${entry.item.name}`}>
                  {entry.item.title}
                </Link>
                <Flag item={entry.item} />
              </h3>
              {entry.item.body && (
                <p className="page-quadrant__entry-text">
                  {excerpt(entry.item.body)}{" "}
                  <Link
                    className="page-quadrant__more"
                    pageName={`${entry.item.quadrant}/${entry.item.name}`}
                  >
                    Read more
                  </Link>
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    )}
  </section>
);

export default function PageQuadrant({
  leaving,
  onLeave,
  pageName,
  items,
  config,
}: PageQuadrantProps) {
  const { slug } = useRadar();
  const { pageHelp } = useMessages();
  const navigate = useNavigate();
  const [searchParamState] = useSearchParamState(undefined, {
    parseOptions: { decode: false },
  });
  const { tags, release } = searchParamState;
  const [highlighted, setHighlighted] = React.useState<number | undefined>();

  const entries = React.useMemo(
    () => numberedEntries(featuredOnly(items), config, pageName),
    [items, config, pageName]
  );

  /** Only the blip click needs the URL as a string: it navigates by hand. */
  const href = (entry: NumberedEntry) =>
    radarHref(slug, `${entry.item.quadrant}/${entry.item.name}`, {
      tags,
      release,
    });

  /** Ring descriptions are published with the radar, in `messages.json`. */
  const describeRing = (ring: string) =>
    pageHelp?.rings?.find(
      (published) => published.name.toLowerCase() === ring.toLowerCase()
    )?.description;

  const visibleRings = config.rings.filter(
    (ring, index) =>
      config.showEmptyRings || entriesOfRing(entries, index).length > 0
  );

  return (
    <Fadeable leaving={leaving} onLeave={onLeave}>
      <SetTitle title={translate(config, pageName)} />
      <HeadlineGroup>
        <HeroHeadline>{translate(config, pageName)}</HeroHeadline>
      </HeadlineGroup>

      <div className="page-quadrant">
        <div className="page-quadrant__chart">
          <QuadrantChart
            entries={entries}
            config={config}
            quadrant={pageName}
            highlighted={highlighted}
            onHighlight={setHighlighted}
            onSelect={(entry) => navigate(href(entry))}
          />
          <Legend />
          <p className="page-quadrant__total">
            {entries.length} technologies in this{" "}
            {Object.keys(config.quadrants).length > 4 ? "segment" : "quadrant"},
            plotted by ring.
          </p>
        </div>

        <div className="page-quadrant__list">
          {/*
            A segment can legitimately hold nothing at a given quarter - the AI
            radar's tooling segment is empty at its baseline. Both radars publish
            `showEmptyRings: false`, so without this the column would render as a
            blank half-page and read as a bug.
          */}
          {entries.length === 0 && (
            <p className="page-quadrant__vacant">
              Nothing in this{" "}
              {Object.keys(config.quadrants).length > 4
                ? "segment"
                : "quadrant"}{" "}
              at the selected quarter. Elyadata had not started working in it
              yet, or the entries arrived later — use the timeline on the radar
              to move forward in time.
            </p>
          )}
          {visibleRings.map((ring) => (
            <RingSection
              key={ring}
              ring={ring}
              description={describeRing(ring)}
              entries={entriesOfRing(entries, config.rings.indexOf(ring))}
              highlighted={highlighted}
              onHighlight={setHighlighted}
            />
          ))}
        </div>
      </div>
    </Fadeable>
  );
}
